import React from 'react';
import { PropTypes } from 'prop-types';
import {
  DateSelectorV2, AutocompleteText,
} from '../FormInputs';
import CustomSelect from '../FormInputs/CustomSelect/CustomSelect';
import Button from '../Button/Button';
import { SITE_TYPES } from '../../constants/sites';
import SelectIconDropdown from '../FormInputs/SelectIconDropdown/SelectIconDropdown';

const AdvancedSearch = ({
  sitesAttributes = {}, params, setParams,
}) => {
  const {
    county,
    city,
    state,
    zip_code: zipCode,
    region,
    tag_name: tagName,
    contact_name: contactName,
    contact_company: contactCompany,
    contact_type: contactType,
    season,
  } = sitesAttributes;
  const getOptions = (array) => array?.map(item => ({ label: item, value: item }));

  const buildOptions = (array) => ['', ...(array || [])].map(item => ({
    label: item || 'Select...',
    value: item,
  }));

  const clearAllFilters = () => {
    setParams({ name: '' });
  };

  const zipOptions = getOptions(zipCode);
  const countyOptions = getOptions(county);
  const cityOptions = getOptions(city);
  const stateOptions = getOptions(state);
  const regionOptions = getOptions(region);
  const tagOptions = getOptions(tagName);
  const contactNameOptions = getOptions(contactName);
  const contactCompanyOptions = getOptions(contactCompany);
  const statusOptions = buildOptions(['Active', 'Inactive', 'Review']);
  const priorityOptions = buildOptions(['High', 'Medium', 'Low', 'No priority']);
  const siteTypeOptions = buildOptions(SITE_TYPES);
  const H2AOptions = buildOptions(['Yes', 'No', 'No data']);
  const visitOptions = buildOptions(['Visited', 'Attempted', 'Not Visited']);
  const contactTypeOptions = buildOptions(contactType);
  const seasonOptions = buildOptions(season);

  return (
    <div>
      <div className="overflow-y-auto max-h-[65vh]">
        <div className="flex items-center pb-4 pt-4">
          <h3 className="text-md font-bold mr-1">Filters</h3>
          <div className="flex text-sm items-center">
            <span>(</span>
            <button
              type="button"
              className="text-vamos-blue underline p-0 m-0 leading-none"
              onClick={clearAllFilters}
            >
              clear all
            </button>
            <span>)</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Location Group */}
          <div className="col-span-1 md:col-span-1">
            <div className="text-base font-bold mb-3">Location</div>
            <div className="grid grid-cols-1 md:grid-cols-1">
              <div className="mb-3">
                <AutocompleteText
                  label="County"
                  name="county"
                  inputId="county-input"
                  options={countyOptions}
                  value={countyOptions?.find(opt => opt.value === params.county) || null}
                  onChange={v => setParams(prev => ({ ...prev, county: v?.value || '' }))}
                />
              </div>
              <div className="mb-3">
                <AutocompleteText
                  label="City"
                  inputId="city-input"
                  options={cityOptions}
                  value={cityOptions?.find(opt => opt.value === params.city) || null}
                  onChange={v => setParams(prev => ({ ...prev, city: v?.value || '' }))}
                />
              </div>
              <div className="mb-3">
                <AutocompleteText
                  label="State"
                  inputId="state-input"
                  options={stateOptions}
                  isMulti
                  value={stateOptions?.filter(opt => params.state?.split(',').includes(opt.value)) || []}
                  onChange={selected => setParams(prev => ({ ...prev, state: selected?.map(s => s.value).join(',') || '' }))}
                />
              </div>
              <div className="mb-3">
                <AutocompleteText
                  label="ZIP"
                  inputId="zip-input"
                  value={zipOptions?.find(opt => opt.value === params.zip_code) || null}
                  onChange={v => setParams(prev => ({ ...prev, zip_code: v?.value || '' }))}
                  options={zipOptions}
                />
              </div>
              <div className="mb-3">
                <AutocompleteText
                  label="Region"
                  inputId="region-input"
                  options={regionOptions}
                  isMulti
                  value={regionOptions?.filter(opt => params.region?.split(',').includes(opt.value)) || []}
                  onChange={selected => setParams(prev => ({ ...prev, region: selected?.map(s => s.value).join(',') || '' }))}
                />
              </div>
            </div>
          </div>

          {/* Details Group */}
          <div className="col-span-1 md:col-span-2">
            <div className="text-base/4 font-bold mb-3">Details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mb-4">
              <div className="mb-3" data-testid="status-select">
                <CustomSelect
                  inputHeight={40}
                  label="Status"
                  name="status"
                  options={statusOptions}
                  value={params.status}
                  setValue={v => setParams(prev => ({ ...prev, status: v?.value || '' }))}
                  inputPadding="0 4px 0 10px"
                  paddingForInput="0 0"
                />
              </div>
              <div className="mb-4" data-testid="priority-select">
                <CustomSelect
                  inputHeight={40}
                  label="Priority"
                  name="priority"
                  options={priorityOptions}
                  value={params.priority}
                  setValue={v => setParams(prev => ({ ...prev, priority: v?.value || '' }))}
                  inputPadding="0 4px 0 10px"
                  paddingForInput="0 0"
                />
              </div>
              <div className="mb-3" data-testid="site-type-select">
                <CustomSelect
                  inputHeight={40}
                  label="Type"
                  name="details-type"
                  options={siteTypeOptions}
                  value={params.site_type}
                  setValue={v => setParams(prev => ({ ...prev, site_type: v?.value || '' }))}
                  inputPadding="0 4px 0 10px"
                  paddingForInput="0 0"
                />
              </div>
              <div className="mb-3">
                <AutocompleteText
                  data-testid="tag-select"
                  label="Tag"
                  options={tagOptions}
                  value={tagOptions?.find(opt => opt.value === params.tag_name) || null}
                  onChange={v => setParams(prev => ({ ...prev, tag_name: v?.value || '' }))}
                />
              </div>
              <div className="mb-3" data-testid="h2a-select">
                <CustomSelect
                  inputHeight={40}
                  label="H2A"
                  name="h2a"
                  options={H2AOptions}
                  value={params.h2a}
                  setValue={v => setParams(prev => ({ ...prev, h2a: v?.value || '' }))}
                  inputPadding="0 4px 0 10px"
                  paddingForInput="0 0"
                />
              </div>
              <div className="mb-3" data-testid="icon-select">
                <SelectIconDropdown
                  inputHeight={40}
                  labelMarginBottom="mb-1"
                  label="Icon"
                  value={params.icon}
                  setValue={v => setParams(prev => ({ ...prev, icon: v.value }))}
                />
              </div>
            </div>

            {/* Worker Presence Group */}
            <div className="col-span-1 md:col-span-1">
              <div className="text-base/4 font-bold mb-4 pt-3 mt-9 leading-5">Worker Presence</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mb-4">
                <div className="mb-3">
                  <DateSelectorV2
                    inputHeight="h-10"
                    label="Arrival"
                    name="arrival"
                    value={params.arrival}
                    setValue={v => setParams(prev => ({ ...prev, arrival: v ?? '' }))}
                    shouldDefaultDate={false}
                    iconRight={4}
                    iconBottom={4}
                    inputStyle={{
                      paddingLeft: '12px',
                      paddingRight: '12px',
                    }}
                    customLabelClasses="text-sm font-sans font-normal"
                    spaceY="space-y-1"
                    isAnchored
                  />
                </div>
                <div className="mb-3">
                  <DateSelectorV2
                    inputHeight="h-10"
                    label="Departure"
                    name="departure"
                    value={params.departure}
                    setValue={v => setParams(prev => ({ ...prev, departure: v ?? '' }))}
                    shouldDefaultDate={false}
                    iconRight={4}
                    iconBottom={4}
                    inputStyle={{
                      paddingLeft: '12px',
                      paddingRight: '12px',
                    }}
                    customLabelClasses="text-sm font-sans font-normal"
                    spaceY="space-y-1"
                    isAnchored
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Group */}
          <div className="col-span-1 md:col-span-1">
            <div className="text-base/4 font-bold mb-4">Contact</div>
            <div className="grid grid-cols-1 md:grid-cols-1 mb-4">
              <div className="mb-3" data-testid="contact-name-select">
                <AutocompleteText
                  label="Name"
                  inputId="contact-name"
                  options={contactNameOptions}
                  value={contactNameOptions?.find(opt => opt.value === params.contact_name) || null}
                  onChange={v => setParams(prev => ({ ...prev, contact_name: v?.value || '' }))}
                />
              </div>
              <div className="mb-3">
                <AutocompleteText
                  label="Company"
                  options={contactCompanyOptions}
                  value={contactCompanyOptions?.find(opt => opt.value === params.contact_company) || null}
                  onChange={v => setParams(prev => ({ ...prev, contact_company: v?.value || '' }))}
                />
              </div>
              <div className="mb-3">
                <CustomSelect
                  inputHeight={40}
                  label="Type"
                  name="contact-type"
                  options={contactTypeOptions}
                  value={params.contact_type}
                  setValue={v => setParams(prev => ({ ...prev, contact_type: v?.value || '' }))}
                  inputPadding="0 4px 0 10px"
                  paddingForInput="0 0"
                  dataTestId="contact-type"
                />
              </div>
            </div>
          </div>

          {/* Visit History Group */}
          <div className="col-span-1 md:col-span-2">
            <div className="text-base/4 font-bold mb-4">Visit History</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mb-9">
              <div className="mb-3" data-testid="visit-status-select">
                <CustomSelect
                  inputHeight={40}
                  label="Visit Status"
                  name="visit-status"
                  options={visitOptions}
                  value={params.visited}
                  setValue={v => setParams(prev => ({ ...prev, visited: v?.value || '' }))}
                  inputPadding="0 4px 0 10px"
                  paddingForInput="0 0"
                />
              </div>
              <div className="mb-3" data-testid="season-select">
                <CustomSelect
                  inputHeight={40}
                  label="In Season"
                  name="in-season"
                  options={seasonOptions}
                  value={params.season_name}
                  setValue={v => setParams(prev => ({ ...prev, season_name: v?.value || '' }))}
                  inputPadding="0 4px 0 10px"
                  paddingForInput="0 0"
                />
              </div>
            </div>

            {/* Created Date Group */}
            <div className="col-span-1 md:col-span-1">
              <div className="text-base/4 font-bold my-4 pt-3 leading-5">Created Date</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mb-4">
                <div className="mb-3">
                  <DateSelectorV2
                    inputHeight="h-10"
                    label="From"
                    name="from-date"
                    value={params.created_from}
                    setValue={v => setParams(prev => ({ ...prev, created_from: v ?? '' }))}
                    shouldDefaultDate={false}
                    iconRight={4}
                    iconBottom={4}
                    inputStyle={{
                      paddingLeft: '12px',
                      paddingRight: '12px',
                    }}
                    customLabelClasses="text-sm font-sans font-normal"
                    spaceY="space-y-1"
                  />
                </div>
                <div className="mb-3">
                  <DateSelectorV2
                    inputHeight="h-10"
                    label="To"
                    name="end-date"
                    value={params.created_to}
                    setValue={v => setParams(prev => ({ ...prev, created_to: v ?? '' }))}
                    shouldDefaultDate={false}
                    iconRight={4}
                    iconBottom={4}
                    inputStyle={{
                      paddingLeft: '12px',
                      paddingRight: '12px',
                    }}
                    customLabelClasses="text-sm font-sans font-normal"
                    spaceY="space-y-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t py-5 border-vamos-blue-gray flex justify-end px-5.5">
        <Button type="submit" label="Search" dataTestId="search-submit" />
      </div>
    </div>
  );
};

AdvancedSearch.propTypes = {
  sitesAttributes: PropTypes.object,
  params: PropTypes.object.isRequired,
  setParams: PropTypes.func.isRequired,
};

export default AdvancedSearch;
